import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { DEFAULT_MENUS, type NavItem, type NavLocation, type NavMenu } from "@/lib/navDefaults";

type Location = NavLocation;

interface RawItem {
  id: string;
  menu_id: string;
  parent_id: string | null;
  label: string;
  link_type: NavItem["link_type"];
  href: string | null;
  open_in_new_tab: boolean;
  action_key: string | null;
  visibility: NavItem["visibility"];
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  is_protected: boolean;
  sort_order: number;
}

interface RawMenu {
  id: string;
  location: Location;
  title: string | null;
  sort_order: number;
  is_active: boolean;
  nav_menu_items: RawItem[];
}

const bySort = (a: { sort_order: number }, b: { sort_order: number }) => a.sort_order - b.sort_order;

/** Build a one-level-deep tree from a flat item list. */
export function buildTree(rows: RawItem[]): NavItem[] {
  const roots: NavItem[] = [];
  const byId = new Map<string, NavItem>();
  for (const r of [...rows].sort(bySort)) {
    byId.set(r.id, { ...r, children: [] });
  }
  for (const node of byId.values()) {
    const parent = node.parent_id ? byId.get(node.parent_id) : null;
    if (parent && parent.id !== node.id) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

export async function fetchNavMenus(location: Location): Promise<NavMenu[]> {
  // nav_menus lives in the standalone database; generated types lag behind.
  const { data, error } = await (supabase as any)
    .from("nav_menus")
    .select("id, location, title, sort_order, is_active, nav_menu_items(*)")
    .eq("location", location)
    .order("sort_order");
  if (error) throw error;
  return ((data ?? []) as unknown as RawMenu[]).sort(bySort).map((m) => ({
    id: m.id,
    location: m.location,
    title: m.title,
    sort_order: m.sort_order,
    is_active: m.is_active,
    items: buildTree(m.nav_menu_items ?? []),
  }));
}

function isVisible(item: NavItem, isSignedIn: boolean, now: number): boolean {
  if (!item.is_active) return false;
  if (item.visibility === "logged_in" && !isSignedIn) return false;
  if (item.visibility === "logged_out" && isSignedIn) return false;
  if (item.starts_at && new Date(item.starts_at).getTime() > now) return false;
  if (item.ends_at && new Date(item.ends_at).getTime() < now) return false;
  return true;
}

function filterMenus(menus: NavMenu[], isSignedIn: boolean): NavMenu[] {
  const now = Date.now();
  return menus
    .filter((m) => m.is_active)
    .map((m) => ({
      ...m,
      items: m.items
        .filter((i) => isVisible(i, isSignedIn, now))
        .map((i) => ({ ...i, children: i.children.filter((c) => isVisible(c, isSignedIn, now)) })),
    }))
    .filter((m) => m.items.length > 0);
}

/**
 * Public navigation for a location. Falls back to the built-in defaults
 * whenever the query fails or no menu has been configured, so the header and
 * footer can never render empty.
 */
export function useNavMenu(location: Location) {
  const { user } = useAuth();
  const { data, isError, isLoading } = useQuery({
    queryKey: ["nav-menus", location],
    queryFn: () => fetchNavMenus(location),
    // Menus change rarely; cache them long enough that in-session navigation
    // and repeat visits never wait on (or visibly swap) the nav.
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });

  const configured = !isError && data && data.length > 0 ? data : null;
  const source = configured ?? DEFAULT_MENUS[location];
  return {
    menus: filterMenus(source, !!user),
    isDefault: !configured,
    // True only on a cold, uncached first load. Call sites hold the nav slot
    // empty for that moment instead of painting defaults that are about to be
    // replaced by the admin-managed menu.
    isLoading: isLoading && !data,
  };
}
